import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const links = [
  { to: "/", label: "Home" },
  { to: "/classes", label: "Open Classes" },
  { to: "/private-bookings", label: "Private Bookings" },
  { to: "/gallery", label: "Gallery" },
];

const INSTAGRAM_URL = "https://www.instagram.com/salumitucson/";

export default function Navigation({ theme = "auto" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const isHome = pathname === "/";
  const transparentTop = isHome && !scrolled;

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-700 ${
        scrolled ? "py-2" : "py-3"
      } ${
        transparentTop
          ? "bg-transparent"
          : "bg-ivory/80 backdrop-blur-xl border-b border-espresso/10"
      }`}
    >
      <nav className="max-w-[1480px] mx-auto px-6 md:px-10 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center group shrink-0"
          aria-label="Salumi Tucson — home"
        >
          <img
            src="/media/salumi-logo-trans.png"
            alt="Salumi Tucson"
            className={`${scrolled ? "h-14 md:h-20" : "h-20 md:h-32"} w-auto transition-all duration-500 ${
              transparentTop
                ? "drop-shadow-[0_4px_18px_rgba(0,0,0,0.7)]"
                : "invert"
            } group-hover:opacity-80 will-change-transform`}
            loading="eager"
          />
          <span className="sr-only">Salumi Tucson</span>
        </Link>

        <ul className="hidden md:flex items-center gap-6 lg:gap-9">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.22em] editorial-underline transition-colors ${
                    transparentTop ? "text-ivory/90 hover:text-ivory" : "text-espresso/80 hover:text-espresso"
                  } ${isActive ? "!text-terracotta" : ""}`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-4 lg:gap-5">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Salumi Tucson on Instagram"
            data-testid="nav-instagram"
            className={`inline-flex items-center justify-center size-10 transition-all duration-500 ${
              transparentTop
                ? "text-ivory/85 hover:text-ivory hover:bg-ivory/10"
                : "text-espresso/75 hover:text-espresso hover:bg-espresso/5"
            } rounded-full`}
          >
            <Instagram strokeWidth={1.5} className="size-[18px]" />
          </a>
          <Link
            to="/private-bookings#contact"
            data-testid="nav-book-button"
            className={`inline-flex items-center px-6 py-3 text-[11px] uppercase tracking-[0.22em] transition-all duration-500 ${
              transparentTop
                ? "bg-ivory text-espresso hover:bg-terracotta hover:text-ivory"
                : "bg-espresso text-ivory hover:bg-terracotta"
            }`}
          >
            Book a Private Experience
          </Link>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Salumi Tucson on Instagram"
            data-testid="nav-instagram-mobile"
            className={`inline-flex items-center justify-center p-2 ${
              transparentTop ? "text-ivory" : "text-espresso"
            }`}
          >
            <Instagram strokeWidth={1.5} className="size-5" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className={`p-2 ${transparentTop ? "text-ivory" : "text-espresso"}`}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu strokeWidth={1.25} className="size-6" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-50 bg-espresso text-ivory flex flex-col"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between px-6 py-6">
              <img src="/media/salumi-logo-trans.png" alt="Salumi" className="h-16 w-auto" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X strokeWidth={1.25} className="size-7" />
              </button>
            </div>
            <ul className="flex-1 flex flex-col justify-center items-start gap-8 px-10">
              {links.map((l, i) => (
                <motion.li
                  key={l.to}
                  initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    className="font-serif text-5xl text-ivory hover:text-terracotta transition-colors"
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="pt-8 flex items-center gap-6"
              >
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="inline-flex items-center justify-center size-12 border border-ivory/30 text-ivory hover:bg-ivory hover:text-espresso transition-colors rounded-full"
                  data-testid="mobile-nav-instagram"
                >
                  <Instagram strokeWidth={1.5} className="size-5" />
                </a>
                <Link
                  to="/private-bookings#contact"
                  className="inline-flex items-center px-8 py-4 bg-terracotta text-ivory text-xs uppercase tracking-[0.22em]"
                  data-testid="mobile-nav-book"
                >
                  Book a Private Experience
                </Link>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
